/**
 * The trend of a figure month by month: a straight line fitted through the
 * months that have a figure (least squares), and how fast it climbs or falls.
 *
 * The last month is still going on, so it is left out of the fit: an
 * incomplete month would pull the line down. With fewer than `minPoints`
 * months to go on there is no trend, since a line through two or three
 * points only misleads.
 */

const FLAT_BELOW = 1; // a change of less than this % a month is "stable"

/**
 * @param {(number|null)[]} values one per month, oldest first, the last month in progress; null: no figure
 * @param {{minPoints?: number}} [options]
 * @returns {{
 *   at: (index: number) => number,
 *   slope: number,
 *   percent: number|null,
 *   points: number,
 * }|null} `at` is the line's value at a month (the last one included, as a projection); `percent` the
 *   change per month as a share of the average of the months used (null when that average isn't above zero)
 */
export const linearTrend = (values, {minPoints = 4} = {}) => {
    const points = /** @type {[number, number][]} */ (
        values
            .slice(0, -1)
            .map((value, index) => [index, value])
            .filter(([, value]) => value !== null)
    );
    const n = points.length;
    if (n < minPoints) return null;

    const sumX = points.reduce((sum, [x]) => sum + x, 0);
    const sumY = points.reduce((sum, [, y]) => sum + y, 0);
    const sumXY = points.reduce((sum, [x, y]) => sum + x * y, 0);
    const sumXX = points.reduce((sum, [x]) => sum + x * x, 0);
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    const mean = sumY / n;

    return {
        at: (index) => intercept + slope * index,
        slope,
        percent: mean > 0 ? Math.round((slope / mean) * 100 * 10) / 10 : null,
        points: n,
    };
};

/**
 * Which way a trend goes, for how to say and color it.
 * @param {number} percent change per month, in %
 * @returns {"up"|"down"|"flat"}
 */
export const direction = (percent) => (percent >= FLAT_BELOW ? "up" : percent <= -FLAT_BELOW ? "down" : "flat");
